import { useCallback, useContext, useEffect, useMemo, useState } from "react";
import { AddressInput, Cart } from "src/types/ctgraphql.d";
import {
  PaymentChannel,
  PaymentMethod,
  TokenPaymentInput,
} from "src/types/graphql.d";
import GooglePay, {
  GooglePayUserData,
} from "src/molecules/ExpressCheckout/GooglePay";
import { useSession } from "next-auth/client";
import { Iso2LetterLocale } from "lib/Intl";
import { useShippingMethodsByLocation } from "src/hooks/useShippingMethodsByLocation";
import useCreateTokenPayment from "src/hooks/useCreateTokenPayment";
import { useRouter } from "next/router";
import useUpdateCartWithExpressCheckoutData from "src/hooks/useUpdateCartWithExpressCheckoutData";
import ScreenLoader from "src/molecules/ScreenLoader/ScreenLoader";
import RedirectForm from "src/molecules/ExpressCheckout/RedirectForm";
import useSubmitPaymentDetails from "src/hooks/useSubmitPaymentDetails";
import { Modal } from "src/atoms/Modal";
import { useCreateCart } from "src/hooks/useCreateCart";
import { AppCtx } from "src/contexts/app.context";

export type ExpressCheckoutUserData = GooglePayUserData;

interface ExpressCheckoutProps {
  cart: Cart;
  activePaymentMethods: PaymentMethod[];
  onError: (error?: unknown) => void;
}

export interface RedirectData {
  MD: string;
  PaReq: string;
  TermUrl: string;
  method: string;
  url: string;
}

const ExpressCheckout = ({
  cart,
  activePaymentMethods,
  onError,
}: ExpressCheckoutProps): JSX.Element | null => {
  const { storeConfig, locale } = useContext(AppCtx);
  const router = useRouter();
  const [session] = useSession();
  const { createCart } = useCreateCart({
    refetchQueries: ["cart"],
    onCompleted: () => {
      void router.push({
        pathname: "/checkout",
        query: {
          step: 3,
        },
      });
    },
  });
  const {
    createTokenPayment,
    data: createTokenPaymentData,
    loading: createTokenPaymentLoading,
    error: createTokenPaymentError,
  } = useCreateTokenPayment({ refetchQueries: ["cart"] });
  const {
    updateCartWithExpressCheckoutData,
    error: updateCartWithExpressCheckoutDataError,
    loading: updateCartWithExpressCheckoutDataLoading,
  } = useUpdateCartWithExpressCheckoutData({ refetchQueries: ["cart"] });
  const { data: shippingMethodsData } = useShippingMethodsByLocation({
    country: cart.shippingAddress?.country as Iso2LetterLocale,
  });
  const {
    submitPaymentDetails,
    data: submitPaymentDetailsData,
    loading: submitPaymentDetailsLoading,
    error: submitPaymentDetailsError,
  } = useSubmitPaymentDetails({ refetchQueries: ["cart"] });
  const [expressCheckoutError, setExpressCheckoutError] = useState<unknown>();
  const [redirectData, setRedirectData] = useState<RedirectData | undefined>();
  const defaultShippingMethod = useMemo(
    () =>
      shippingMethodsData?.shippingMethodsByLocation.find(
        (shippingMethod) => shippingMethod.key === "default-shipping"
      ),
    [shippingMethodsData?.shippingMethodsByLocation]
  );
  const loading = useMemo(
    () =>
      createTokenPaymentLoading ||
      updateCartWithExpressCheckoutDataLoading ||
      submitPaymentDetailsLoading,
    [
      createTokenPaymentLoading,
      updateCartWithExpressCheckoutDataLoading,
      submitPaymentDetailsLoading,
    ]
  );

  useEffect(() => {
    if (createTokenPaymentData) {
      const { createTokenPayment } = createTokenPaymentData;
      if (createTokenPayment.result_code === "Authorised") {
        void createCart({
          currency: storeConfig.currencyCode,
        });
      }
      if (createTokenPayment.result_code === "RedirectShopper") {
        if (createTokenPayment.action) {
          setRedirectData({
            ...createTokenPayment.action.data,
            method: createTokenPayment.action.method,
            url: createTokenPayment.action.url,
          });
        }
      }
    }
  }, [createTokenPaymentData, router, createCart, storeConfig.currencyCode]);

  useEffect(() => {
    const { redirectResult, threeDSResult } = router.query;
    if (redirectResult || threeDSResult) {
      try {
        if (typeof redirectResult === "string") {
          void submitPaymentDetails({
            redirect_result: redirectResult,
          });
        }
        if (typeof threeDSResult === "string") {
          void submitPaymentDetails({
            three_ds_result: threeDSResult,
          });
        }
      } catch (e) {
        setExpressCheckoutError(e);
        console.error("Cannot submit payment details", e);
      }
    }
  }, [router.query, submitPaymentDetails]);

  useEffect(() => {
    if (submitPaymentDetailsData) {
      const { submitPaymentDetails } = submitPaymentDetailsData;
      if (submitPaymentDetails.success) {
        void createCart({
          currency: storeConfig.currencyCode,
        });
      } else {
        setExpressCheckoutError("submitPaymentDetails error");
        console.error("submitPaymentDetails error", submitPaymentDetailsError);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    router,
    submitPaymentDetailsData,
    submitPaymentDetailsData?.submitPaymentDetails,
    submitPaymentDetailsError,
  ]);

  useEffect(() => {
    if (expressCheckoutError) {
      onError(expressCheckoutError);
    }
  }, [expressCheckoutError, onError]);

  useEffect(() => {
    const apiError =
      updateCartWithExpressCheckoutDataError ||
      createTokenPaymentError ||
      submitPaymentDetailsError;
    if (apiError) {
      const errors = apiError.graphQLErrors
        .map((graphQLError) => graphQLError.message)
        .join(", ");
      if (window.NREUM) {
        window.NREUM.noticeError(new Error(`ExpressCheckout: ${errors}`));
      }
      setExpressCheckoutError(errors);
      console.error("API error", apiError);
    }
  }, [
    createTokenPaymentError,
    submitPaymentDetailsError,
    updateCartWithExpressCheckoutDataError,
  ]);

  const updateCartInformation = useCallback(
    async (expressCheckoutUserData: ExpressCheckoutUserData) => {
      const { shippingAddress, billingAddress, customerEmail } =
        expressCheckoutUserData;

      const email = session?.user.email || customerEmail || "";

      const finalShippingAddress: AddressInput = {
        email,
        phone: shippingAddress?.phoneNumber,
        streetName: `${shippingAddress?.address1 || ""} ${
          shippingAddress?.address2 || ""
        } ${shippingAddress?.address3 || ""}`,
        postalCode: shippingAddress?.postalCode,
        city: shippingAddress?.locality,
        state: !(locale === "it-it" || locale === "es-es")
          ? shippingAddress?.administrativeArea
          : "",
        country: shippingAddress?.countryCode,
        firstName: shippingAddress?.name,
        lastName: "",
      };
      const finalBillingAddress: AddressInput = {
        email,
        phone: billingAddress?.phoneNumber,
        streetName: `${billingAddress?.address1 || ""} ${
          billingAddress?.address2 || ""
        } ${billingAddress?.address3 || ""}`,
        postalCode: billingAddress?.postalCode,
        city: billingAddress?.locality,
        state: !(locale === "it-it" || locale === "es-es")
          ? billingAddress?.administrativeArea
          : "",
        country: billingAddress?.countryCode,
        firstName: billingAddress?.name,
        lastName: "",
      };

      try {
        setExpressCheckoutError("");
        await updateCartWithExpressCheckoutData(
          finalShippingAddress,
          finalBillingAddress,
          email,
          defaultShippingMethod?.id ||
            cart.shippingInfo?.shippingMethod?.id ||
            ""
        );
      } catch (e) {
        setExpressCheckoutError(e);
        console.error(
          "Cannot update cart with express checkout data from the Google form",
          e
        );
      }
    },
    [
      cart.shippingInfo?.shippingMethod?.id,
      defaultShippingMethod?.id,
      session?.user.email,
      updateCartWithExpressCheckoutData,
    ]
  );

  const callCreateTokenPayment = useCallback(
    async (userData: ExpressCheckoutUserData) => {
      const data: TokenPaymentInput = {
        cart_id: cart.id,
        cart_version: cart.version as number,
        return_url: `${window.location.origin}/cart`,
        channel: PaymentChannel.Web,
        payment_method: userData.payment_method,
        token: userData.token,
        browser_info: {
          user_agent: window.navigator.userAgent,
          color_depth: window.screen.colorDepth,
          // https://developer.mozilla.org/en-US/docs/Web/API/Navigator/javaEnabled
          java_enabled: false,
          language: window.navigator.language,
          screen_height: window.screen.height,
          screen_width: window.screen.width,
          timezone_offset: new Date().getTimezoneOffset(),
        },
      };
      try {
        await createTokenPayment(data);
      } catch (e) {
        setExpressCheckoutError(e);
        console.error("Cannot create payment token", e);
      }
    },
    [cart.id, cart.version, createTokenPayment]
  );

  const handleExpressCheckoutPaymentReady = useCallback(
    async (expressCheckoutUserData: ExpressCheckoutUserData) => {
      await updateCartInformation(expressCheckoutUserData);
      if (
        !updateCartWithExpressCheckoutDataError &&
        !updateCartWithExpressCheckoutDataLoading
      ) {
        await callCreateTokenPayment(expressCheckoutUserData);
      }
    },
    [
      updateCartInformation,
      updateCartWithExpressCheckoutDataError,
      updateCartWithExpressCheckoutDataLoading,
      callCreateTokenPayment,
    ]
  );

  return loading ? (
    <Modal onClose={() => {}} overFlow={"visible"} isOpen={true}>
      <ScreenLoader />
    </Modal>
  ) : (
    <>
      {defaultShippingMethod && (
        <>
          {activePaymentMethods.includes(PaymentMethod.GooglePay) && (
            <GooglePay
              cart={cart}
              onPaymentReady={(data) =>
                void handleExpressCheckoutPaymentReady(data)
              }
              onError={(error) => setExpressCheckoutError(error)}
            />
          )}
          {redirectData && <RedirectForm redirectData={redirectData} />}
        </>
      )}
    </>
  );
};

export default ExpressCheckout;
